import { Check } from 'lucide-react';
import { Button } from '@ncoder/ui';
import { cn } from '@/lib/utils';
import { BILLING_PLANS, SEAT_PLAN_LABEL } from '@/services/org-admin';
import type { PlanTier, PurchasablePlan, SeatPlanTally } from '@/services/org-admin';

/**
 * The plan catalogue.
 *
 * ── Why a column-anchored matrix rather than cards or a plain table ──────────
 *
 * There are three plans and they differ in exactly three comparable numbers. Cards put those numbers
 * in separate boxes where they cannot be read across, and leave enough whitespace to invite invented
 * feature bullets. A bare table reads them across perfectly and gives the eye nothing to land on —
 * the first version of this was correct, dense and completely flat.
 *
 * So: a real `<table>` for the semantics and the row alignment, with each plan column given its own
 * surface, border and radius so it reads as a thing you can buy. It deliberately does NOT use
 * `.oa-table`, which is the module's RECORDS table — sticky uppercase headers, `table-layout: fixed`,
 * hover rows. Every one of those is wrong here, and the previous version was overriding them one at a
 * time (`normal-case`, `text-left`) rather than declining the whole recipe.
 *
 * ── The current plan ────────────────────────────────────────────────────────
 *
 * A plan belongs to a SEAT, so "which plan is this organization on" has no single answer — the honest
 * answer is a distribution, and the "In use" row is it. In the common case of a small team all on one
 * plan, exactly one column lights up and it reads precisely like "current plan"; in a mixed org it
 * tells the truth that a single badge could not.
 *
 * The counts come from `tallySeatPlans` (services/org-admin/seatPlan.ts), which buckets the `plan`
 * field the billing API reports per member. They were once inferred from each seat's AI budget, back
 * when the API carried no plan — so this section used to carry a footnote disclosing the matching
 * method. The field is authoritative now, there is no method left to disclose, and the footnote is
 * deleted rather than reworded.
 */

/** One line per plan on what it is for, phrased from the two figures and nothing else. */
const PLAN_FIT: Record<PurchasablePlan, string> = {
  starter: 'Trying nCoder on real work',
  growth: 'Regular day-to-day use',
  scale: 'Heavy or full-time use',
};

function formatUsd(amount: number): string {
  return `$${amount}`;
}

/** How much AI budget a dollar buys. The one comparable that is not already on screen. */
function budgetPerDollar(tier: PlanTier): number {
  return tier.budgetUsd / tier.priceUsd;
}

const BEST_RATIO = Math.max(...BILLING_PLANS.map(budgetPerDollar));

function formatRatio(tier: PlanTier): string {
  // One decimal, because 1.0× / 2.0× / 3.0× lines up in a column and "3x" does not read as a rate.
  return `${budgetPerDollar(tier).toFixed(1)}×`;
}

function membersLabel(count: number): string {
  return count === 1 ? '1 member' : `${count} members`;
}

export interface PlanMatrixProps {
  /** Narrow container — stacked blocks rather than columns. Driven by the panel's ResizeObserver. */
  isNarrow: boolean;
  canManage: boolean;
  /** Blocks the CTA while the seat list is still loading, since a purchase needs a member. */
  isLoadingSeats: boolean;
  /** Seats matched to each plan. `null` until the seat list has loaded. */
  tally: SeatPlanTally | null;
  /**
   * Plans that no remaining seat could take, because every active member already holds them under a
   * live subscription. Resolved by the panel, which owns the seat join.
   *
   * This is what "the organization is already on this plan" means in a product where a plan belongs to
   * a SEAT: not "somebody has it" — another member may still want it — but "there is nobody left to
   * buy it for". In a single-member org that is exactly "this is my current plan".
   */
  plansAtCapacity: readonly PurchasablePlan[];
  disabledReason: string;
  onChoosePlan: (plan: PurchasablePlan) => void;
}

export function PlanMatrix({
  isNarrow,
  canManage,
  plansAtCapacity,
  isLoadingSeats,
  tally,
  disabledReason,
  onChoosePlan,
}: PlanMatrixProps) {
  const seatCount = tally
    ? tally.byPlan.starter + tally.byPlan.growth + tally.byPlan.scale + tally.free + tally.unknown
    : 0;
  const hasNoSeats = tally !== null && seatCount === 0;

  const blocked = !canManage || isLoadingSeats || hasNoSeats;
  const blockedReason = !canManage
    ? disabledReason
    : isLoadingSeats
      ? 'Loading members…'
      : hasNoSeats
        ? 'There is no active member to buy a plan for'
        : undefined;

  const countFor = (plan: PurchasablePlan) => tally?.byPlan[plan] ?? 0;
  const isAtCapacity = (plan: PurchasablePlan) => plansAtCapacity.includes(plan);
  const shared = { blocked, blockedReason, countFor, isAtCapacity, onChoose: onChoosePlan };

  return (
    <section className="oa-panel" aria-label="Plans">
      <header className="oa-toolbar flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-2.5">
        <h3 className="oa-title">Plans</h3>
        {/*
          The single most important sentence in this section.

          Every admin arriving here carries the tiered-SaaS assumption — that the organization is on a
          plan and this is where it gets upgraded. It is not: a plan is bought per member, and two
          people in the same org routinely sit on different ones. Correcting that assumption once, in
          the header, prevents every later confusion about why there is no org-wide "Upgrade" button.
        */}
        <p className="oa-meta min-w-0 flex-1 basis-64">
          Each member has their own plan and AI budget — there is no organization-wide tier.
        </p>
      </header>

      <div className="p-[var(--oa-gutter)] pt-3">
        {isNarrow ? (
          <div className="flex flex-col gap-2">
            {BILLING_PLANS.map((tier) => (
              <PlanBlock key={tier.plan} tier={tier} isLoadingSeats={isLoadingSeats} {...shared} />
            ))}
          </div>
        ) : (
          <table
            className="w-full"
            // `separate` with a horizontal gap is what turns three sets of cells into three columns
            // the eye reads as objects. Vertical spacing stays 0 so a column is one continuous card.
            style={{ borderCollapse: 'separate', borderSpacing: '8px 0' }}
          >
            <caption className="sr-only">
              Available plans, what each costs, the AI budget it grants, how much budget each dollar
              buys, and how many of this organization&rsquo;s members are on each
            </caption>
            <colgroup>
              <col style={{ width: '23%' }} />
              <col span={3} />
            </colgroup>
            <thead>
              <tr>
                {/* Empty by design: this column holds row labels, not data about a plan, so a word
                    here would describe nothing. */}
                <th scope="col">
                  <span className="sr-only">Comparing</span>
                </th>
                {BILLING_PLANS.map((tier) => {
                  const inUse = countFor(tier.plan) > 0;
                  return (
                    <th
                      key={tier.plan}
                      scope="col"
                      className={cn(
                        'rounded-t-[var(--oa-radius-md)] border-x border-t px-3 pb-2.5 pt-3 text-left align-bottom',
                        columnSurface(inUse),
                      )}
                    >
                      {/*
                        `min-h` reserves the badge's line box whether or not the badge is there.

                        Without it the header grew ~4px the moment the counts arrived, because the
                        badge is taller than the plan name beside it — so the whole catalogue shifted
                        on load. Reserving the taller of the two heights costs nothing when no badge
                        renders and keeps the section's geometry identical before and after.
                      */}
                      <span className="flex min-h-[20px] flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-[15px] font-semibold leading-none text-foreground">
                          {SEAT_PLAN_LABEL[tier.plan]}
                        </span>
                        {inUse && <InUseBadge />}
                      </span>
                      <span className="oa-meta mt-1.5 block font-normal">{PLAN_FIT[tier.plan]}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <MatrixRow
                label="You pay"
                hint="Per member"
                countFor={countFor}
                render={(tier) => (
                  /* The one promoted figure on the screen. Two large numbers in the same column is
                     exactly how "you pay $50" and "you get $100" get mistaken for each other. */
                  <span className="text-[24px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
                    {formatUsd(tier.priceUsd)}
                  </span>
                )}
              />
              <MatrixRow
                label="AI budget"
                hint="Spend ceiling granted"
                countFor={countFor}
                render={(tier) => (
                  <span className="text-[15px] font-semibold tabular-nums">
                    {formatUsd(tier.budgetUsd)}
                  </span>
                )}
              />
              <MatrixRow
                label="Budget per $1"
                hint="What a dollar buys"
                countFor={countFor}
                render={(tier) => (
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[13px] font-semibold tabular-nums">
                      {formatRatio(tier)}
                    </span>
                    {budgetPerDollar(tier) === BEST_RATIO && <BestValueBadge />}
                  </span>
                )}
              />
              <MatrixRow
                label="In use"
                hint="Members on this plan"
                countFor={countFor}
                render={(tier) => (
                  <InUseCell count={countFor(tier.plan)} isLoading={isLoadingSeats} />
                )}
              />
              <tr>
                <th scope="row" className="text-left align-top">
                  <span className="sr-only">Buy this plan</span>
                </th>
                {BILLING_PLANS.map((tier) => (
                  <td
                    key={tier.plan}
                    className={cn(
                      'rounded-b-[var(--oa-radius-md)] border-x border-b px-3 pb-3 pt-1 align-top',
                      columnSurface(countFor(tier.plan) > 0),
                    )}
                  >
                    <ChoosePlanButton tier={tier} {...shared} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        )}
      </div>

      <div className="oa-footer-rail flex flex-col gap-1 py-2">
        <p className="oa-meta">
          Every plan is billed monthly, and the budget resets at the start of each billing cycle.
        </p>
        {/*
          Stated because its absence is otherwise read as a missing feature. There is no downgrade
          operation in the API.
        */}
        <p className="oa-meta">
          To move a member to a smaller plan, cancel their subscription and buy the new one once the
          current period ends.
        </p>
      </div>
    </section>
  );
}

/**
 * The column's surface.
 *
 * One accent, spent on the only thing an admin needs to find in under two seconds. The other columns
 * get a plain raised surface rather than a competing tint — three emphasised columns is no emphasis.
 */
function columnSurface(inUse: boolean): string {
  return inUse
    ? 'border-primary/45 bg-primary/[0.06]'
    : 'border-[var(--oa-divider)] bg-[var(--oa-surface)]';
}

/**
 * Neutral-but-strong rather than accent-coloured.
 *
 * `text-primary` on `bg-primary/15` looked right in light mode and washed out to pale grey in dark:
 * the studio shell remaps `--primary` to a monochrome charcoal, so an accent-tinted badge has almost
 * no colour to lend. A strong neutral IS this theme's accent, and it also keeps the badge from
 * competing with the genuinely green "Most budget" marker two rows below.
 */
function InUseBadge() {
  return (
    <span className="oa-micro inline-flex items-center gap-1 rounded-full bg-foreground/10 px-1.5 py-0.5 text-foreground">
      <Check className="h-2.5 w-2.5" aria-hidden />
      In use
    </span>
  );
}

function BestValueBadge() {
  return (
    <span className="oa-micro rounded-full bg-success/10 px-1.5 py-px text-success-strong">
      Most budget
    </span>
  );
}

/**
 * All three states at ONE text size.
 *
 * The loading dash and "Nobody yet" were `oa-meta` (12px) while the count was 13px, so the row grew
 * 4px when billing resolved — a shift the geometry check in qa/billing.mjs caught and which no
 * screenshot would have. Only the colour and the weight change now; the line box does not.
 */
function InUseCell({ count, isLoading }: { count: number; isLoading: boolean }) {
  if (isLoading) return <span className="text-[13px] text-muted-foreground">—</span>;
  return count === 0 ? (
    <span className="text-[13px] text-muted-foreground">Nobody yet</span>
  ) : (
    <span className="text-[13px] font-medium text-foreground">{membersLabel(count)}</span>
  );
}

/** A label + hint row header, so the matrix reads without the reader holding the column meaning. */
function MatrixRow({
  label,
  hint,
  countFor,
  render,
}: {
  label: string;
  hint: string;
  countFor: (plan: PurchasablePlan) => number;
  render: (tier: PlanTier) => React.ReactNode;
}) {
  return (
    <tr>
      {/* `align-middle`, because a two-line label beside a 24px figure reads as misaligned when both
          are top-anchored — the label's first line sits above the number's cap height. */}
      <th scope="row" className="py-2.5 pr-3 text-left align-middle">
        <span className="block text-[13px] font-medium text-foreground">{label}</span>
        <span className="oa-meta mt-0.5 block font-normal">{hint}</span>
      </th>
      {BILLING_PLANS.map((tier) => (
        <td
          key={tier.plan}
          className={cn('border-x px-3 py-2.5 align-top', columnSurface(countFor(tier.plan) > 0))}
        >
          {render(tier)}
        </td>
      ))}
    </tr>
  );
}

interface ChoosePlanButtonProps {
  tier: PlanTier;
  blocked: boolean;
  blockedReason?: string;
  countFor: (plan: PurchasablePlan) => number;
  isAtCapacity: (plan: PurchasablePlan) => boolean;
  onChoose: (plan: PurchasablePlan) => void;
}

function ChoosePlanButton({
  tier,
  blocked,
  blockedReason,
  isAtCapacity,
  onChoose,
}: ChoosePlanButtonProps) {
  /*
   * A state marker, not a disabled verb.
   *
   * When nobody is left to buy this plan for, "Buy Growth" is not an action that happens to be
   * unavailable — it is not an action at all, and a greyed-out button still invites the click that
   * explains itself only on hover. Saying "Current plan" states the situation instead, which is also
   * what it literally is in a single-member organization.
   */
  if (isAtCapacity(tier.plan)) {
    return (
      <p className="flex h-8 items-center justify-center gap-1.5 text-[13px] font-medium text-foreground">
        <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
        Current plan
      </p>
    );
  }

  return (
    <Button
      type="button"
      /*
       * Outline on every column, including the one in use.
       *
       * A solid button there was meant to match the column's emphasis and did the opposite: the
       * studio shell's monochrome `--primary` renders `default` as a pale lavender slab in dark mode,
       * which read as washed-out rather than emphatic. The column surface and the badge already carry
       * the hierarchy, and three identical buttons keep the CTA row from arguing for a tier.
       */
      variant="outline"
      size="sm"
      className="h-8 w-full"
      disabled={blocked}
      // A disabled control that does not say why is a dead end. The panel already has a reason to
      // hand, so it goes on the button rather than being left for the admin to work out.
      title={blocked ? blockedReason : undefined}
      onClick={() => onChoose(tier.plan)}
    >
      {/* Names the plan, so each button has its own accessible name rather than three "Buy"s. */}
      Buy {SEAT_PLAN_LABEL[tier.plan]}
    </Button>
  );
}

/** The narrow-container form: one block per plan, same figures, no horizontal comparison. */
function PlanBlock({
  tier,
  blocked,
  blockedReason,
  countFor,
  isAtCapacity,
  onChoose,
  isLoadingSeats,
}: ChoosePlanButtonProps & { isLoadingSeats: boolean }) {
  const count = countFor(tier.plan);
  const inUse = count > 0;

  return (
    <div
      className={cn(
        'flex flex-col gap-2.5 rounded-[var(--oa-radius-md)] border p-3',
        columnSurface(inUse),
      )}
    >
      <div className="min-w-0">
        {/* Same height reservation as the table header, for the same reason. */}
        <p className="flex min-h-[20px] flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[14px] font-semibold">{SEAT_PLAN_LABEL[tier.plan]}</span>
          {inUse && <InUseBadge />}
        </p>
        <p className="oa-meta">{PLAN_FIT[tier.plan]}</p>
      </div>

      {/* Two labelled figures, never one number — the same rule the purchase dialog follows. */}
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
        <div>
          <dt className="oa-micro">You pay</dt>
          <dd className="text-[19px] font-semibold leading-tight tabular-nums">
            {formatUsd(tier.priceUsd)}
          </dd>
        </div>
        <div>
          <dt className="oa-micro">AI budget</dt>
          <dd className="text-[19px] font-semibold leading-tight tabular-nums">
            {formatUsd(tier.budgetUsd)}
          </dd>
        </div>
      </dl>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="oa-meta">Budget per $1</span>
        <span className="text-[13px] font-semibold tabular-nums">{formatRatio(tier)}</span>
        {budgetPerDollar(tier) === BEST_RATIO && <BestValueBadge />}
        <span className="oa-meta">
          · {isLoadingSeats ? '—' : count === 0 ? 'Nobody yet' : membersLabel(count)}
        </span>
      </div>

      <ChoosePlanButton
        tier={tier}
        blocked={blocked}
        blockedReason={blockedReason}
        countFor={countFor}
        isAtCapacity={isAtCapacity}
        onChoose={onChoose}
      />
    </div>
  );
}
